import { Section } from '@/components/console/Section';
import type { AwarenessCheckRow } from '@/lib/people-data';
import s from '@/pages/console/console.module.css';

/**
 * Awareness checks (pv-auth GET /provider/admin/people/:id `awarenessChecks`;
 * command-center FUNCTIONAL_EXAMS_2026-10-24): the functional check runs,
 * newest first, dates and means only. The neck centre test's mean is how far
 * from centre the head came to rest with the eyes closed, in degrees; null is
 * not measured, never zero, with the reason key. Words-free: nothing the
 * member said is here. Absent from the detail until pv-auth #474 is deployed,
 * and the section says so.
 */
const TEST_LABEL: Record<string, string> = { neck_centre: 'Neck centre' };
const label = (key: string) => key.replace(/_/g, ' ');
const deg = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(1)} deg` : null);

export function AwarenessChecksSection({ rows }: { rows: AwarenessCheckRow[] | undefined }) {
  const count = rows ? rows.length : null;
  const summary =
    rows === undefined ? 'not in this pv-auth yet (deploy #474)' : rows.length === 0 ? 'none yet' : 'neck centre: mean error in degrees, eyes closed, 4.5 is the published reference; newest first';
  return (
    <Section title="Awareness checks" summary={summary} count={count} defaultOpen={!!rows && rows.length > 0} id="awareness-checks">
      {rows === undefined && <div className={s.empty}>This pv-auth does not send awarenessChecks yet.</div>}
      {rows && rows.length === 0 && <div className={s.empty}>No awareness check yet.</div>}
      {rows && rows.length > 0 && (
        <dl className={s.facts}>
          {rows.map((r, i) => {
            const mean = deg(r.meanErrorDeg);
            return (
              <div key={`${r.localDate}-${i}`} style={{ display: 'contents' }} data-testid={`awareness-${i}`}>
                <dt>
                  {r.localDate} · {TEST_LABEL[r.testKey] ?? label(r.testKey)}
                </dt>
                <dd>
                  {mean ? (
                    <>
                      {mean} (left {deg(r.leftMeanDeg) ?? 'not measured'}, right {deg(r.rightMeanDeg) ?? 'not measured'}; {r.sampleCount} turns read)
                    </>
                  ) : (
                    <span className={s.missing}>
                      not measured{r.notMeasuredReason ? ` (${label(r.notMeasuredReason)})` : ''}; {r.sampleCount} turns read
                    </span>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </Section>
  );
}
